/**
 * SBSIntegrationWrapper — owns all SBS orchestration logic so that main.tsx
 * doesn't need to know about SBS internals.
 *
 * This component:
 *  - Checks the `rovo-ext_sbs_enabled` feature flag
 *  - Tracks the streaming → idle transition to derive `isRovoResponseComplete`
 *  - Lazily loads SBSIntegration via React.lazy + Suspense
 */

import React, { useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import Suspense from '@atlaskit/react-ufo/suspense';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';

// Lazy-loaded — never bundled with the main entry point
const LazySBSIntegration = React.lazy(
	() =>
		import(
			/* webpackChunkName: "@atlassian/conversation-assistant/sbs-integration" */ './SBSIntegration'
		),
);

export interface SBSIntegrationWrapperProps {
	userId: string;
	siteId: string;
	product: string;
	conversationId: string;
	/** The conversation status value from the chat store (e.g. idle, awaitingResponse, typingResponse). */
	conversationStatus: CHAT_STORE_STATUS | undefined;
}

/**
 * Wrapping component that gates SBS behind its feature flag and derives
 * `isRovoResponseComplete` from the conversation status lifecycle.
 *
 * Renders nothing when the flag is off or the product is not 'rovo'.
 */
export function SBSIntegrationWrapper({
	userId,
	siteId,
	product,
	conversationId,
	conversationStatus,
}: SBSIntegrationWrapperProps): React.JSX.Element | null {
	// Track the streaming → idle transition to derive isRovoResponseComplete.
	// The conversation status goes through: idle → awaitingResponse → typingResponse → idle.
	// We only report "complete" after the conversation has been in a streaming state
	// (awaitingResponse or typingResponse) and then returns to idle. This prevents
	// a false positive on initial load when the status is already idle.
	const hasSeenStreamingRef = useRef(false);
	useEffect(() => {
		if (
			conversationStatus === CHAT_STORE_STATUS.awaitingResponse ||
			conversationStatus === CHAT_STORE_STATUS.typingResponse
		) {
			hasSeenStreamingRef.current = true;
		}
	}, [conversationStatus]);

	// Gate on feature flag — render nothing when disabled
	if (!fg('rovo-ext_sbs_enabled')) {
		return null;
	}

	const isRovoResponseComplete =
		product === 'rovo' &&
		conversationStatus === CHAT_STORE_STATUS.idle &&
		hasSeenStreamingRef.current;

	return (
		<Suspense fallback={null} interactionName="conversation-assistant-sbs-suspense">
			<LazySBSIntegration
				userId={userId}
				siteId={siteId}
				product={product}
				isRovoResponseComplete={isRovoResponseComplete}
				conversationId={conversationId}
			/>
		</Suspense>
	);
}
